import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../api/axiosInstance";
import { isAuthenticated } from "../../utils/auth";

const normalizeMatch = (rawMatch) => {
  if (!rawMatch) return rawMatch;

  const sportIdRaw = rawMatch.sport_id ?? rawMatch.event_type_id;
  const sportIdNum =
    sportIdRaw != null && !Number.isNaN(Number(sportIdRaw))
      ? Number(sportIdRaw)
      : undefined;

  const inPlayRaw = rawMatch.in_play ?? rawMatch.inplay;
  const inPlayNum =
    inPlayRaw === 1 ||
    inPlayRaw === "1" ||
    inPlayRaw === true ||
    inPlayRaw === "true"
      ? 1
      : 0;

  return {
    // keep all original fields
    ...rawMatch,
    // normalized/common fields used by UI components
    event_id:
      rawMatch.event_id ?? rawMatch.match_id ?? rawMatch.id ?? rawMatch._id,
    name:
      rawMatch.name ??
      rawMatch.match_name ??
      rawMatch.market_name ??
      rawMatch.series_name ??
      "",
    competition_name:
      rawMatch.competition_name ?? rawMatch.series_name ?? rawMatch.sport_name,
    event_type_id: rawMatch.event_type_id ?? sportIdNum,
    open_date:
      rawMatch.open_date ?? rawMatch.match_date ?? rawMatch.open_date_format,
    in_play: inPlayNum,
    tv_channel:
      rawMatch.tv_channel ??
      (rawMatch.has_tv_url || rawMatch.match_tv_url ? "1" : null),
    bm_active:
      rawMatch.bm_active != null
        ? rawMatch.bm_active
        : rawMatch.bookmaker_count > 0
          ? 1
          : 0,
    fancy_active:
      rawMatch.fancy_active != null
        ? rawMatch.fancy_active
        : rawMatch.enable_fancy
          ? 1
          : 0,
    custom_active: rawMatch.custom_active ?? "",
    featured_event:
      rawMatch.featured_event != null
        ? rawMatch.featured_event
        : rawMatch.featured_event === true
          ? 1
          : 0,
  };
};

const initialState = {
  matches: [],
  matchesBySport: {},
  loading: false,
  initialLoad: true,
  error: null,
};

export const fetchMatchesAsync = createAsyncThunk(
  "matches/fetchMatches",
  async (sportId, { rejectWithValue }) => {
    try {
      const loggedIn = isAuthenticated();
      const payload = { sport_id: String(sportId) };

      const response = loggedIn
        ? await axiosInstance.post("/match/homeMatchesV2", payload)
        : await axiosInstance.post("/match/homeMatchesOpen", payload);

      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch matches",
      );
    }
  },
);

export const fetchHomeAllMatches = createAsyncThunk(
  "matches/fetchHomeAllMatches",
  async (_, { rejectWithValue }) => {
    try {
      const isLoggedIn = isAuthenticated();

      const response = isLoggedIn
        ? await axiosInstance.post("/match/homeMatchesV2")
        : await axiosInstance.get("/match/homeMatchesOpen");

      return response.data || response;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch matches",
      );
      
    }
  },
);



const matchesSlice = createSlice({
  name: "matches",
  initialState,
  reducers: {
    clearMatches: (state) => {
      state.matches = [];
      state.matchesBySport = {};
      state.error = null;
      state.initialLoad = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMatchesAsync.pending, (state) => {
        if (state.initialLoad) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchMatchesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.initialLoad = false;
        const sportId = action.meta?.arg;

        const raw = action.payload;
        let matchesArray = [];
        if (Array.isArray(raw)) {
          matchesArray = raw;
        } else if (Array.isArray(raw?.data)) {
          matchesArray = raw.data;
        } else if (Array.isArray(raw?.matches)) {
          matchesArray = raw.matches;
        }

        const normalized = matchesArray.map(normalizeMatch);
        const sortedMatches =
          normalized.sort(
            (a, b) =>
              new Date(a.match_date || a.open_date) -
              new Date(b.match_date || b.open_date),
          ) || [];

        if (sportId != null && !Number.isNaN(Number(sportId))) {
          const key = String(sportId);
          state.matchesBySport[key] = sortedMatches;
          state.matches = Object.values(state.matchesBySport).flat();
        } else {
          state.matches = sortedMatches;
        }
        state.error = null;
      })
      .addCase(fetchMatchesAsync.rejected, (state, action) => {
        state.loading = false;
        state.initialLoad = false;
        state.error = action.payload;
      })
      .addCase(fetchHomeAllMatches.pending, (state) => {
        if (state.initialLoad) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(fetchHomeAllMatches.fulfilled, (state, action) => {
        state.loading = false;
        state.initialLoad = false;

        const raw = action.payload;

        let matchesArray = [];
        if (Array.isArray(raw)) {
          matchesArray = raw;
        } else if (Array.isArray(raw?.data)) {
          matchesArray = raw.data;
        } else if (Array.isArray(raw?.matches)) {
          matchesArray = raw.matches;
        }

        const normalized = matchesArray.map(normalizeMatch);

        const matchesBySport = normalized.reduce((acc, match) => {
          const sportId = match.event_type_id ?? match.sport_id;
          if (!sportId) return acc;
          const key = String(sportId);
          if (!acc[key]) {
            acc[key] = [];
          }
          acc[key].push(match);
          return acc;
        }, {});

        Object.keys(matchesBySport).forEach((key) => {
          matchesBySport[key] =
            matchesBySport[key].sort(
              (a, b) =>
                new Date(a.match_date || a.open_date) -
                new Date(b.match_date || b.open_date),
            ) || [];
        });

        state.matchesBySport = matchesBySport;
        state.matches = Object.values(matchesBySport).flat();
        state.error = null;
      })
      .addCase(fetchHomeAllMatches.rejected, (state, action) => {
        state.loading = false;
        state.initialLoad = false;
        state.error = action.payload;
      });
  },
});

export const { clearMatches } = matchesSlice.actions;
export default matchesSlice.reducer;
